<template>
  <div class="view-page">
    <title-bar title="Tickets" :slotData="true">
      <template slot="content-actions">
        <button class="button is-intelligential" @click="openCreateModal">
          Nuevo ticket
        </button>
      </template>
    </title-bar>

    <div class="tickets-filters">
      <div class="tickets-filters__control">
        <label class="tickets-filters__label" for="tickets-status-filter">Filtrar por estatus</label>
        <div class="select">
          <select id="tickets-status-filter" v-model="statusFilter" @change="getTicketList(1)">
            <option value="">Todos los estatus</option>
            <option v-for="status in statusOptions" :key="status.code" :value="status.code">{{ status.label }}</option>
          </select>
        </div>
      </div>
    </div>

    <tickets-list
      :tickets="tickets"
      :pagination="pagination"
      :getTickets="getTicketList"
      :isLoading="isLoading"
      @ticket-closed="handleTicketClosed"
    />

    <modal-component
      :active="isModalOpen"
      @cancel="closeModal"
      titleModal="Nuevo ticket"
      optionalText="Describe tu incidencia y el equipo de soporte te dará seguimiento."
      @hdlOnClick="submitForm"
      :loading="isSaving"
      :loader="isSaving"
      :isOverflow="false"
      textSave="Crear ticket"
    >
      <new-ticket-form
        ref="newTicketForm"
        :module-options="moduleOptions"
        :priority-options="priorityOptions"
        :type-options="typeOptions"
        :loading="isSaving"
        @submit="createNewTicket"
      />
    </modal-component>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { MODULE_OPTIONS, PRIORITY_OPTIONS, TYPE_OPTIONS, STATUS_OPTIONS } from '@/constants/tickets'

export default {
  name: 'Tickets',
  components: {
    TitleBar: () => import('@/components/ui/TitleBar.vue'),
    ModalComponent: () => import('@/components/ui/ModalComponent'),
    TicketsList: () => import('@/components/Tickets/TicketsList.vue'),
    NewTicketForm: () => import('@/components/Tickets/NewTicketForm.vue')
  },
  data () {
    return {
      isLoading: true,
      isSaving: false,
      isModalOpen: false,
      tickets: [],
      pagination: {
        pageNumber: 1,
        totalPages: 1
      },
      statusFilter: '',
      moduleOptions: MODULE_OPTIONS,
      priorityOptions: PRIORITY_OPTIONS,
      typeOptions: TYPE_OPTIONS,
      statusOptions: STATUS_OPTIONS,
      perPage: 10
    }
  },
  async mounted () {
    await this.getTicketList()
  },
  methods: {
    ...mapActions(['getTickets', 'createTicket', 'createTicketEvidence', 'addTemporalNotification']),
    async getTicketList (page = 1) {
      this.isLoading = true
      try {
        const response = await this.getTickets({
          pageNumber: page,
          perPage: this.perPage,
          statusCode: this.statusFilter || undefined
        })
        if (response) {
          this.tickets = response.tickets || []
          this.pagination = {
            pageNumber: page,
            totalPages: response.pagination?.totalPages || Math.max(1, Math.ceil((response.pagination?.total || 0) / this.perPage))
          }
        }
      } finally {
        this.isLoading = false
      }
    },
    openCreateModal () {
      this.isModalOpen = true
    },
    closeModal () {
      this.isModalOpen = false
      if (this.$refs.newTicketForm) {
        this.$refs.newTicketForm.reset()
      }
    },
    submitForm () {
      this.$refs.newTicketForm.submit()
    },
    async createNewTicket (input) {
      const { evidenceFiles, ...ticketInput } = input
      this.isSaving = true
      const ticket = await this.createTicket(ticketInput)
      this.isSaving = false
      if (ticket) {
        this.closeModal()
        await this.getTicketList(1)
        // El ticket ya se creó exitosamente en este punto (modal cerrado,
        // lista refrescada). La subida de evidencia corre aparte y no debe
        // bloquear ni revertir la percepción de éxito si alguna falla.
        if (evidenceFiles && evidenceFiles.length) {
          this.uploadEvidenceFiles(ticket.id, evidenceFiles)
        }
      }
    },
    async uploadEvidenceFiles (ticketId, files) {
      const results = await Promise.all(files.map(async (file) => {
        try {
          const fileBase64 = await this.readFileAsBase64(file)
          return await this.createTicketEvidence({
            ticketId,
            fileBase64,
            fileName: file.name,
            contentType: file.type
          })
        } catch (err) {
          console.error(`No se pudo subir la evidencia "${file.name}" del ticket ${ticketId}`, err)
          return null
        }
      }))
      const failedCount = results.filter(result => !result).length
      if (failedCount) {
        this.addTemporalNotification({
          type: 'warning',
          message: failedCount === files.length
            ? 'El ticket se creó, pero no se pudo subir la evidencia adjunta.'
            : `El ticket se creó, pero ${failedCount} archivo(s) de evidencia no se pudieron subir.`
        })
      }
    },
    readFileAsBase64 (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const result = e.target.result || ''
          resolve(result.split(',').pop())
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },
    handleTicketClosed (updatedTicket) {
      const ticket = this.tickets.find(t => t.id === updatedTicket.id)
      if (ticket) {
        ticket.statusCode = updatedTicket.statusCode
        ticket.statusName = updatedTicket.statusName
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.tickets-filters {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
}

.tickets-filters__control {
  align-items: center;
  display: flex;
  gap: 0.6rem;
}

.tickets-filters__label {
  color: #5f7081;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}
</style>
